import { supabase } from '@/lib/supabase';

/**
 * Заметки исследований для блока «Что говорят учёные» (29.09.2026).
 *
 * 🔴 ГЛАВНОЕ: ссылки берутся ТОЛЬКО отсюда. Модель не сочиняет ни авторов,
 * ни журнал, ни год — выдуманный DOI выглядит настоящим, проверить его на глаз
 * невозможно, а отвечает за это имя Славы как специалиста.
 *
 * Заполняется миграцией `054_research_notes.sql`, разбор каждой работы —
 * `docs/research-notes.md`.
 */

export type ResearchNote = {
  id: string;
  kind: 'nutrient' | 'behavior';
  nutrient_key: string | null;
  trigger_key: string | null;
  claim: string;
  detail: string;
  hook: string;
  authors: string;
  journal: string;
  year: number;
  design: string;
  participants: number | null;
  evidence_level: 'A' | 'B' | 'C' | 'D';
  url: string;
  caveat: string | null;
};

/**
 * Подпись под каждой карточкой с наукой.
 *
 * Требование Славы 29.09.2026: не вселять сомнения и страх. Мы подсвечиваем
 * пользу и говорим, что норма набирается едой, а всё остальное — к врачу.
 */
export const RESEARCH_DISCLAIMER =
  'Это не медицинская рекомендация. Норму по большинству нутриентов закрывает обычная еда, ' +
  'а вопросы про анализы, добавки и самочувствие стоит обсуждать с лечащим врачом.';

/** Как называем тип работы человеку, чтобы слабое не звучало как сильное */
export function evidenceLabel(n: ResearchNote): string {
  const n_ = n.participants ? n.participants.toLocaleString('ru-RU') : null;
  switch (n.evidence_level) {
    case 'A': return n_ ? `Мета-анализ, ${n_} участников` : 'Мета-анализ исследований';
    case 'B': return n_ ? `Эксперимент, ${n_} участников` : 'Рандомизированный эксперимент';
    case 'C': return n_ ? `Наблюдение, ${n_} человек` : 'Наблюдательное исследование';
    default:  return 'Наблюдение, выводы предварительные';
  }
}

// Справочник маленький и меняется редко — держим в памяти вкладки,
// чтобы открытие разбора не ходило в сеть каждый раз.
const cache = new Map<string, ResearchNote | null>();

async function fetchOne(column: 'nutrient_key' | 'trigger_key', value: string) {
  const key = `${column}:${value}`;
  if (cache.has(key)) return cache.get(key) ?? null;
  const { data } = await supabase
    .from('research_notes')
    .select('*')
    .eq(column, value)
    .eq('active', true)
    .limit(1)
    .maybeSingle();
  const note = (data as ResearchNote) ?? null;
  cache.set(key, note);
  return note;
}

/** Заметка под нутриент приёма или недели */
export function getNoteForNutrient(nutrientKey: string) {
  return fetchOne('nutrient_key', nutrientKey);
}

/**
 * Заметка под событие: `activity_up`, `steps_day`, `checkin_sleep`,
 * `streak_5`, `habit_21`, `weight_second`.
 */
export function getNoteForTrigger(trigger: string) {
  return fetchOne('trigger_key', trigger);
}
